import { useState } from "react";
import { Box, Button, CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import { IfCanAccess } from "@react-admin/ra-rbac";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { BASE_API_URL } from "../../../config/configUrl";
import { getAccessToken } from "../../../admin/authProvider";

const postTransferAction = (id: string | number, action: "approve" | "reject") =>
  fetch(`${BASE_API_URL}/gasstation/transfers/${id}/${action}`, {
    method: "POST",
    credentials: "include",
    headers: new Headers({
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${getAccessToken()}`,
    }),
  }).then(async (response) => {
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || response.statusText);
    }
    return response.json();
  });

function ApprovalActions() {
  const { t }: { t: any } = useTranslation();
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [loadingAction, setLoadingAction] = useState<"approve" | "reject" | null>(null);

  if (!record || record.statusOrder !== "PENDING_APPROVAL") {
    return null;
  }

  const handleAction = async (action: "approve" | "reject") => {
    setLoadingAction(action);
    try {
      await postTransferAction(record.id, action);
      notify(
        action === "approve" ? t("Transfer approved") : t("Transfer rejected"),
        { type: "success" }
      );
      refresh();
    } catch (error: any) {
      notify(`${t("Action failed")}: ${error.message}`, { type: "error" });
      // A refusal (409) or an unknown outcome (503) means the transfer may have moved on:
      // re-read it so the actions reflect its current status.
      refresh();
    } finally {
      setLoadingAction(null);
    }
  };

  return (
    <IfCanAccess action="approve" resource="gasStationTransfers">
      <Box display="flex" gap={2}>
        <Button
          variant="contained"
          color="success"
          startIcon={
            loadingAction === "approve" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CheckRoundedIcon />
            )
          }
          disabled={loadingAction !== null}
          onClick={() => handleAction("approve")}
        >
          {t("Approve")}
        </Button>
        <Button
          variant="contained"
          color="error"
          startIcon={
            loadingAction === "reject" ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <CloseRoundedIcon />
            )
          }
          disabled={loadingAction !== null}
          onClick={() => handleAction("reject")}
        >
          {t("Reject")}
        </Button>
      </Box>
    </IfCanAccess>
  );
}

export default ApprovalActions;
